import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../css/aboutus.css";
import logo from "../images/cscscard.png";
import cubiclesImg from "../images/cubicles.jpg"; // <-- ADD YOUR IMAGE HERE

const cscsCards = [
  {
    type: "Green",
    title: "Green CSCS Card",
    role: "Labourer & General Operative",
    description:
      "The Green Labourer card is required for individuals working as labourers or general operatives on UK construction sites.",
    imgSrc: logo,
    test: "HS&E Labourer Test",
    validity: "5 Years",
  },
];

const About = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: cscsCards.map((card) => ({
      "@type": "Question",
      name: `What is a ${card.title} and what test is required?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${card.description} The required test is: ${card.test}. This card is valid for ${card.validity}.`,
      },
    })),
  };

  return (
    <div className="page-wrapper flat-dark-theme">
      {/* --- SEO METADATA START --- */}
      <Helmet>
        <title>Get Your Green CSCS Card | About The Course</title>
        <meta
          name="description"
          content="Need your Green CSCS card? Visit our official training and test centre office to take your test in our dedicated testing cubicles."
        />
        <meta
          name="keywords"
          content="CSCS card, Green CSCS card, test centre, testing cubicles, construction training course, labourer card, test office"
        />

        {/* Open Graph / Social Media Sharing */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.getyourcscs.co.uk/about" />
        <meta
          property="og:title"
          content="Green CSCS Card Explained | About the Course "
        />
        <meta
          property="og:description"
          content="Visit our dedicated office and test centre facility to complete your training and secure your Green CSCS card in a distraction-free environment."
        />

        <link rel="canonical" href="https://www.getyourcscs.co.uk/about" />

        {/* Inject FAQ Schema Structured Data */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      {/* --- SEO METADATA END --- */}

      <div className="about-container">
        {/* Top Section: Course Info */}
        <section className="about-header-section">
          <h2>About the Course & Training</h2>
          <p className="intro-text">
            Our comprehensive classroom-based sessions and on-site test
            facilities cover everything you need to safely navigate a
            construction site and acquire your certification efficiently.
          </p>

          <div className="course-breakdown-flat">
            <h3>What's Included in Your Package:</h3>
            <ul className="course-features">
              <li>Level 1 Health, Safety and Environment Training</li>
              <li>In-person instruction with qualified assessors</li>
              <li>Dedicated On Site Computer Test Facility</li>
              <li>All registration, booking, and examination fees</li>
            </ul>
          </div>
        </section>

        {/* NEW SECTION: Office & Test Centre Showcase (With Image) */}
        <section className="office-showcase-section">
          <div className="office-content-box">
            <div className="office-text">
              <span className="badge-highlight">Official Facility</span>
              <h3>Visit Our Dedicated Office & Test Centre</h3>
              <p>
                We operate a fully equipped, professional office and examination
                centre designed specifically for construction workers. You don’t
                need to worry about juggling multiple locations—come right to
                our facility to complete your training and sit your touch-screen
                test in one of our{" "}
                <strong>private, distraction free testing cubicles.</strong>
              </p>
              <ul className="office-perks">
                <li>
                  🏢 Individual testing cubicles ensuring privacy and focus
                  during your exam
                </li>
                <li>
                  🤝 Friendly on-site support staff to guide your booking & ID
                  checks
                </li>
                <li>
                  📍 Conveniently located with great transport links and parking
                </li>
              </ul>
            </div>

            {/* Visuals Column: Image + Highlights */}
            <div className="office-visuals">
              <img
                src={cubiclesImg}
                alt="Private Testing Cubicles"
                className="office-cubicles-img"
              />
              <div className="office-highlight-card">
                <div className="highlight-item">
                  <span className="icon">📍</span>
                  <div>
                    <strong>Location & Access</strong>
                    <p>Open Saturdays with limited slots</p>
                  </div>
                </div>
                <div className="highlight-item">
                  <span className="icon">💻</span>
                  <div>
                    <strong>Private Testing Cubicles</strong>
                    <p>Instant results provided upon test completion</p>
                  </div>
                </div>
                <Link to="/booking" className="btn-secondary office-cta-btn">
                  Book Your Visit / Test
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom Section: Single Card Display */}
        <section className="cscs-cards-section">
          <div className="cards-intro">
            <h2>Get Your CSCS Card</h2>
            <p>
              The <strong>Green General CSCS</strong> card is the standard
              requirement for access to{" "}
              <strong>ALL UK construction sites.</strong> Book your course and
              test below to secure your certification.
            </p>
          </div>

          <div className="single-card-container">
            {cscsCards.map((card, index) => (
              <div className="flat-info-card" key={index}>
                <div className="card-header">
                  <span
                    className={`card-color-indicator bg-${card.type.toLowerCase()}`}
                  ></span>
                  <h3>{card.title}</h3>
                </div>

                <h4 className="card-role">{card.role}</h4>

                {card.imgSrc && (
                  <img
                    src={card.imgSrc}
                    alt={`${card.title} Example`}
                    className="card-image"
                  />
                )}

                <p className="card-desc">{card.description}</p>

                <div className="card-meta">
                  <div className="meta-item">
                    <strong>Required Test:</strong>
                    <span>{card.test}</span>
                  </div>
                  <div className="meta-item">
                    <strong>Valid For:</strong>
                    <span>{card.validity}</span>
                  </div>
                </div>

                <Link
                  to="/booking"
                  state={{ cscsCard: card.title }}
                  className="btn-primary card-book-btn"
                >
                  Book {card.type} Card Now
                </Link>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;
